import React, { useContext, useState, useEffect, useMemo } from "react";
import { AlertConstants } from "best-common-react";
import styled from "styled-components";
import WBCPrimaryOutlinedButton from "../../../elements/WBCPrimaryOutlinedButton";
import PropTypes from "prop-types";
import WBCYearsContext from "../../../../contexts/WBCYearsContext";
import InterestListApi from "../../../../httpClients/InterestListApi";
import WBCProfileApi from "../../../../httpClients/WBCProfileApi";
import ProspectivePlayerContext from "../../../../contexts/ProspectivePlayerContext";
import SelectedTeamContext from "../../../../contexts/SelectedTeamContext";
import RouteConstants from "../../../../constants/RouteConstants";
import { withRouter } from "react-router-dom";
import ProspectivePlayersApi from "../../../../httpClients/ProspectivePlayersApi";
import GlobalModalContext from "../../../../contexts/GlobalModalContext";
import { useError } from "../../../hooks/useError";
import { useAlert } from "../../../hooks/useAlert";
import CoachingStaffContext from "../../../../contexts/CoachingStaffContext";
import StaffApi from "../../../../httpClients/StaffApi";
import AuthContext from "../../../../contexts/AuthContext";

const TableButton = styled(WBCPrimaryOutlinedButton)`
  &&& {
    width: 100%;
    height: 100%;
  }
`;

const ButtonText = styled.div`
  padding-top: 4px;
  padding-left: 6px;
`;

const AddInterestContainer = styled.div`
  color: ${props => props.theme[props.isOnInterestList ? "grey" : "wbcBlue"]};
  cursor: ${props => (props.isOnInterestList ? "no-drop" : "pointer")};
  text-align: center;
`;

const PersonMatchModalAddButton = withRouter(({ history, data, toggleModal, personType }) => {
  // hook(s)
  const globalModalContext = useContext(GlobalModalContext);
  const { selectedYear } = useContext(WBCYearsContext);
  const selectedTeamContext = useContext(SelectedTeamContext);
  const prospectivePlayerContext = useContext(ProspectivePlayerContext);
  const { staff } = useContext(CoachingStaffContext).state;
  const authContext = useContext(AuthContext);
  const showErrors = useError();
  const showAlert = useAlert();

  const [isPlayerOnInterestList, setPlayerIsOnInterestList] = useState(false);
  const [isStaffOnFedTeam, setStaffIsOnFedTeam] = useState(false);
  const [addingToInterestList, setAddingToInterestList] = useState(false);
  const [copyingProfile, setCopyingProfile] = useState(false);
  const [importingProfile, setImportingProfile] = useState(false);

  // variables
  const { team } = selectedTeamContext.state;
  const { teamId: fedTeamId } = team;
  const { isWBCIadmin, isFedAdmin, isTeamCoordinator } = authContext.state;
  const interestListNotFinalized = !team.finalizedStatus?.finalizedInterestDate;
  const canAddToInterestList = isWBCIadmin || isTeamCoordinator || (isFedAdmin && interestListNotFinalized);

  useEffect(() => {
    const checkPlayerIsOnInterestList = () => {
      InterestListApi.isOnInterestList(selectedYear, fedTeamId, data.profileId)
        .then(data => {
          typeof data === "boolean" ? setPlayerIsOnInterestList(data) : setPlayerIsOnInterestList(false);
        })
        .catch(response => {
          showAlert(response.toString(), AlertConstants.TYPES.DANGER);
        });
    };

    const checkStaffOnFedTeam = () => {
      const allStaff = [...staff.coaches, ...staff.managers, ...staff.trainers];
      setStaffIsOnFedTeam(allStaff.some(s => s.profileId === data.profileId));
      return false;
    };

    if (data.profileId) {
      personType === RouteConstants.PLAYER ? checkPlayerIsOnInterestList() : checkStaffOnFedTeam();
    }
  }, [data.profileId, selectedYear, fedTeamId, showAlert, staff, personType]);

  // function(s)
  const closeGlobalModal = () => globalModalContext.dispatch({ type: "closeModal" });
  const addToInterestOverride = () => {
    const profileId = data.profileId;
    InterestListApi.addPlayerToInterestList(selectedYear, fedTeamId, profileId, true)
      .then(() => {
        showAlert("Player added to Interest List");
        toggleModal(false);
        prospectivePlayerContext.dispatch({
          type: "addPlayerToInterestList",
          profileId
        });
        closeGlobalModal();
      })
      .catch(({ response }) => {
        showAlert(response.data.message, AlertConstants.TYPES.WARNING);
        toggleModal(false);
        closeGlobalModal();
      });
  };

  // button functions
  const addToInterestList = () => {
    if (!addingToInterestList) {
      setAddingToInterestList(true);

      const profileId = data.profileId;
      InterestListApi.addPlayerToInterestList(selectedYear, fedTeamId, profileId)
        .then(() => {
          setAddingToInterestList(false);
          showAlert("Player added to Interest List");
          prospectivePlayerContext.dispatch({
            type: "addPlayerToInterestList",
            profileId
          });
          toggleModal(false);
        })
        .catch(({ response }) => {
          setAddingToInterestList(false);

          // TODO: make back end return custom error response code
          if (response.data.message.includes("is already on the interest list")) {
            showAlert("This player is already on your Interest List", AlertConstants.TYPES.WARNING);
            toggleModal(false);
          } else {
            showErrors(response.data.message, addToInterestOverride);
          }
        });
    }
  };

  const copyPlayerProfile = () => {
    if (!copyingProfile) {
      setCopyingProfile(true);

      WBCProfileApi.copyPlayerProfileToTeam(fedTeamId, data.profileId)
        .then(() => {
          setCopyingProfile(false);
          showAlert(`${personType === RouteConstants.PLAYER ? "Player" : "Coaching staff member"} saved`);
          toggleModal(false);
          if (personType === RouteConstants.PLAYER) {
            ProspectivePlayersApi.getProspectivePlayers(selectedYear, fedTeamId).then(response => {
              prospectivePlayerContext.dispatch({
                type: "setProspectivePlayers",
                prospectivePlayers: response
              });
            });
          } else {
            StaffApi.getStaff(selectedYear, fedTeamId).then(response => {
              const { coaches, managers, trainers } = response;
              const allStaff = [...coaches, ...managers, ...trainers];
              prospectivePlayerContext.dispatch({
                type: "setStaff",
                staff: allStaff
              });
            });
          }
        })
        .catch(({ response }) => {
          setCopyingProfile(false);
          showAlert(response.data.message, AlertConstants.TYPES.DANGER);
        });
    }
  };

  const importPlayerFromEbis = () => {
    if (!importingProfile) {
      setImportingProfile(true);

      WBCProfileApi.copyEbisPlayerProfileToTeam(fedTeamId, data.ebisId, personType === RouteConstants.STAFF)
        .then(profile => {
          setImportingProfile(false);
          showAlert(personType === RouteConstants.STAFF ? "Staff saved" : "Player saved");
          history.push(
            RouteConstants.PROFILE.replace(":playerId", profile.profileId)
              .replace(":fedTeamId", fedTeamId)
              .replace(":profileType", personType)
          );
        })
        .catch(() => {
          setImportingProfile(false);
          showAlert("Error while saving profile", AlertConstants.TYPES.DANGER);
        });
    }
  };

  // determine which function actually handles the click
  const onClick = () => {
    if (data.isOnOwnFedTeam) return addToInterestList;
    if (!data.profileId) return importPlayerFromEbis;
    return copyPlayerProfile;
  };

  // generate button content
  const buttonContent = () => {
    if (data.isOnOwnFedTeam && personType === RouteConstants.PLAYER && canAddToInterestList) {
      return (
        <AddInterestContainer isOnInterestList={isPlayerOnInterestList}>
          <div className={"d-flex"}>
            <i style={{ marginTop: "6px" }} className="fas fa-user-plus" />
            <ButtonText>Interest List</ButtonText>
          </div>
        </AddInterestContainer>
      );
    }
    return !data.profileId ? "Import Profile" : `Add ${personType === RouteConstants.PLAYER ? "Player" : "Staff"}`;
  };

  const disabledText = useMemo(() => {
    if (!canAddToInterestList && data.isOnOwnFedTeam && personType === RouteConstants.PLAYER) {
      return "Interest List is finalized";
    }
    if ((isPlayerOnInterestList || isStaffOnFedTeam) && data.isOnOwnFedTeam) {
      return "This" + (isStaffOnFedTeam ? " staff member" : " player") + " is already on your team";
    }
    // removing as of 10/7/25 as it confuses tournament years for being on another fed team
    // if (personType === RouteConstants.STAFF && data?.federations?.length >= 1) {
    //   return "This staff member is already on another federation team";
    // }
    return null;
  }, [
    canAddToInterestList,
    isPlayerOnInterestList,
    isStaffOnFedTeam,
    data?.isOnOwnFedTeam,
    data?.federations?.length,
    personType
  ]);

  // component
  return (
    <TableButton style={{ cursor: "pointer" }} title={disabledText} disabled={!!disabledText} onClick={onClick()}>
      {buttonContent()}
    </TableButton>
  );
});

PersonMatchModalAddButton.propTypes = {
  data: PropTypes.object,
  toggleModal: PropTypes.func,
  personType: PropTypes.string
};

export default PersonMatchModalAddButton;
